// UC70-02 — SYSTEM_ADMIN manages the MConnect connection credentials (kind=MCONNECT).
// National, read-only, no registrar; they authenticate the public data API (UC70-03).
// The secret is generated in the browser; only its SHA-256 hash reaches the server.
// An active credential gets a new secret on its own row, client_id kept (TC-REUSE-05-16).
// per docs/shape-ops-system-admin-credential-acts.md §2.3
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslate } from '@tolgee/react';
import {
  MudButton,
  MudIcon,
  MudTextInput,
  MudModal,
  MudInfoBox,
  MudTable,
  MudTag,
  MudTextarea,
} from '@egov-moldova/mud-react';
import { apiClient, apiErrorKey } from '../../app/api-client';
import { useConfirm } from '../../components/ConfirmDialog/ConfirmDialog';
import {
  EmptyRegisterMessage,
  PageHeader,
  formatDateTime,
  onModalDismiss,
  useToast,
} from '@simsm/ui-shared';
import { generateClientSecret } from '../../features/credentials/web-crypto';

interface MconnectCredentialDto {
  id: string;
  clientId: string;
  allowedIps: string[];
  allowedIpCidrs: string[];
  active: boolean;
  createdAtIso: string;
  updatedAtIso: string | null;
  lastUsedAtIso: string | null;
}

interface CreatedCredential {
  credential: MconnectCredentialDto;
  clientSecret: string;
  // A new secret on an existing credential, not a new credential: the dialog
  // that shows it once says which.
  reissued: boolean;
}

const CLIENT_ID_PATTERN = /^[a-zA-Z0-9._-]{8,64}$/;

// mud-table's own column type is not re-exported from the package root.
interface TableColumn {
  key: string;
  label: string;
  align?: 'start' | 'center' | 'end';
}

async function fetchCredentials(): Promise<MconnectCredentialDto[]> {
  const { data } = await apiClient.get<{ credentials: MconnectCredentialDto[] }>(
    '/admin/mconnect-credentials',
  );
  return data.credentials ?? [];
}

async function createCredential(input: {
  clientId: string;
  allowedIps: string[];
  allowedIpCidrs: string[];
}): Promise<CreatedCredential> {
  const { plaintext, sha256Hex } = await generateClientSecret();
  const { data, status } = await apiClient.post<{ credential: MconnectCredentialDto }>(
    '/admin/mconnect-credentials',
    {
      clientId: input.clientId,
      secretSha256Hex: sha256Hex,
      allowedIps: input.allowedIps,
      allowedIpCidrs: input.allowedIpCidrs,
    },
  );
  // 200, not 201: an active credential already held this client_id (the form's
  // own check had no list to compare against) and now has the new secret.
  return { credential: data.credential, clientSecret: plaintext, reissued: status === 200 };
}

// The same POST as a new credential: the server gives the active credential
// holding this client_id the new secret, and the address lists travel
// unchanged so nothing but the secret moves (one MLog event).
async function regenerateSecret(row: MconnectCredentialDto): Promise<CreatedCredential> {
  const { plaintext, sha256Hex } = await generateClientSecret();
  const { data } = await apiClient.post<{ credential: MconnectCredentialDto }>(
    '/admin/mconnect-credentials',
    {
      clientId: row.clientId,
      secretSha256Hex: sha256Hex,
      allowedIps: row.allowedIps,
      allowedIpCidrs: row.allowedIpCidrs,
    },
  );
  return { credential: data.credential, clientSecret: plaintext, reissued: true };
}

async function revokeCredential(clientId: string): Promise<void> {
  await apiClient.delete(`/admin/mconnect-credentials/${encodeURIComponent(clientId)}`);
}

function splitLines(value: string): string[] {
  return value
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}

export function MconnectCredentialsPage(): JSX.Element {
  const { t } = useTranslate();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const toast = useToast();
  const credentials = useQuery({
    queryKey: ['admin-mconnect-credentials'],
    queryFn: fetchCredentials,
  });

  const [emitOpen, setEmitOpen] = useState(false);
  const [clientId, setClientId] = useState('');
  const [allowedIps, setAllowedIps] = useState('');
  const [allowedCidrs, setAllowedCidrs] = useState('');
  const [createdSecret, setCreatedSecret] = useState<CreatedCredential | null>(null);
  const [copied, setCopied] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: createCredential,
    onSuccess: (resp) => {
      setCreatedSecret(resp);
      setEmitOpen(false);
      setClientId('');
      setAllowedIps('');
      setAllowedCidrs('');
      setFormError(null);
      queryClient.invalidateQueries({ queryKey: ['admin-mconnect-credentials'] });
    },
    // The refusal carries a code (CLIENT_ID_TAKEN, CLIENT_ID_INVALID, …); dropping
    // it told every failure the same thing. The fallback still names this operation.
    onError: (err: unknown) =>
      setFormError(t(apiErrorKey(err, 'mconnect_creds.error.create_failed'))),
  });

  const regenerate = useMutation({
    mutationFn: regenerateSecret,
    onSuccess: (resp) => {
      setCreatedSecret(resp);
      void queryClient.invalidateQueries({ queryKey: ['admin-mconnect-credentials'] });
    },
    onError: (err: unknown, row) =>
      toast({
        variant: 'error',
        title: t(apiErrorKey(err, 'mconnect_creds.error.regenerate_failed')),
        text: row.clientId,
      }),
  });

  const revoke = useMutation({
    mutationFn: revokeCredential,
    // Revoking cuts off a caller that is authenticating with these credentials
    // right now, so the outcome is worth a line either way. The refusal had none
    // at all: the row simply stayed as it was.
    onSuccess: (_data, clientId) => {
      void queryClient.invalidateQueries({ queryKey: ['admin-mconnect-credentials'] });
      toast({ variant: 'info', title: t('mconnect_creds.toast.revoked'), text: clientId });
    },
    onError: (err: unknown, clientId) =>
      toast({
        variant: 'error',
        title: t(apiErrorKey(err, 'mconnect_creds.error.revoke_failed')),
        text: clientId,
      }),
  });

  const rows = credentials.data ?? [];
  const columns: TableColumn[] = [
    { key: 'clientId', label: t('credentials.column.client_id') },
    { key: 'ips', label: t('credentials.column.allowed_ips') },
    { key: 'created', label: t('credentials.column.created') },
    { key: 'updated', label: t('credentials.register.column.updated') },
    { key: 'lastUsed', label: t('credentials.column.last_used') },
    { key: 'status', label: t('credentials.column.status') },
    { key: 'actions', label: t('credentials.column.actions'), align: 'end' },
  ];

  function formatLastUsed(iso: string | null): string {
    if (!iso) return t('credentials.never_used');
    return formatDateTime(iso);
  }

  async function copyToClipboard(value: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* noop */
    }
  }

  function submit(): void {
    setFormError(null);
    if (!CLIENT_ID_PATTERN.test(clientId.trim())) {
      setFormError(t('mconnect_creds.error.client_id_invalid'));
      return;
    }
    // The server answers an existing active client_id with a new secret, so a
    // client_id typed here by mistake would cut off a partner still calling.
    // A new secret is the row's own act, behind its own confirmation.
    const existing = rows.find((row) => row.clientId === clientId.trim());
    if (existing) {
      setFormError(
        t(
          existing.active
            ? 'mconnect_creds.error.client_id_exists'
            : 'mconnect_creds.error.client_id_revoked',
        ),
      );
      return;
    }
    create.mutate({
      clientId: clientId.trim(),
      allowedIps: splitLines(allowedIps),
      allowedIpCidrs: splitLines(allowedCidrs),
    });
  }

  return (
    <section className="simsm-page simsm-credentials">
      <PageHeader
        title={t('mconnect_creds.title')}
        lede={t('mconnect_creds.lede')}
        actions={
          <MudButton
            shape="circular"
            variant="primary"
            size="md"
            onClick={() => {
              setFormError(null);
              setEmitOpen(true);
            }}
          >
            <MudIcon slot="icon-start" name="plus-large" size={16} />
            {t('mconnect_creds.action.new')}
          </MudButton>
        }
      />

      {credentials.isError ? (
        <MudInfoBox emphasis="strong" role="alert" variant="error">
          {t('mconnect_creds.error.load_failed')}
        </MudInfoBox>
      ) : (
        <div className="simsm-credentials__table">
          <MudTable
            className="simsm-table"
            columns={columns}
            rows={rows.map((row) => ({
              id: row.id,
              clientId: row.clientId,
              ips: [...row.allowedIps, ...row.allowedIpCidrs].join(', '),
              created: formatLastUsed(row.createdAtIso),
              updated: row.updatedAtIso
                ? formatDateTime(row.updatedAtIso)
                : t('credentials.register.none'),
              lastUsed: formatLastUsed(row.lastUsedAtIso),
              status: row.active ? t('credentials.status.active') : t('credentials.status.revoked'),
              actions: '',
            }))}
            rowStyle="divided"
            disableSort={true}
            aria-label={t('mconnect_creds.table.aria_label')}
          >
            <EmptyRegisterMessage>{t('mconnect_creds.empty')}</EmptyRegisterMessage>
            <span slot="header-cell-actions" className="visually-hidden">
              {t('credentials.column.actions')}
            </span>
            {/* The three date headers are slotted so they may run to two lines, as
                the wireframe's `.tbl.compact th` does: beside the 368px sidebar the
                register has 864px at 1280, and a nowrap header held each date
                column at 120px or more (V-042). */}
            <span slot="header-cell-created" className="simsm-table__th">
              {t('credentials.column.created')}
            </span>
            <span slot="header-cell-updated" className="simsm-table__th">
              {t('credentials.register.column.updated')}
            </span>
            <span slot="header-cell-lastUsed" className="simsm-table__th">
              {t('credentials.column.last_used')}
            </span>
            {rows.map((row, index) => [
              <code
                key={`client-${row.id}`}
                slot={`cell-clientId-${index}`}
                className="simsm-credentials__client-id"
              >
                {row.clientId}
              </code>,
              <span key={`ips-${row.id}`} slot={`cell-ips-${index}`}>
                {[...row.allowedIps, ...row.allowedIpCidrs].join(', ')}
              </span>,
              // A moment wraps between its date and its time rather than holding a
              // column at its full width (V-042).
              <span
                key={`created-${row.id}`}
                slot={`cell-created-${index}`}
                className="simsm-credentials__date"
              >
                {formatLastUsed(row.createdAtIso)}
              </span>,
              <span
                key={`updated-${row.id}`}
                slot={`cell-updated-${index}`}
                className="simsm-credentials__date"
              >
                {row.updatedAtIso
                  ? formatDateTime(row.updatedAtIso)
                  : t('credentials.register.none')}
              </span>,
              <span
                key={`last-used-${row.id}`}
                slot={`cell-lastUsed-${index}`}
                className="simsm-credentials__date"
              >
                {formatLastUsed(row.lastUsedAtIso)}
              </span>,
              <span key={`status-${row.id}`} slot={`cell-status-${index}`}>
                <MudTag
                  variant="status"
                  semantic={row.active ? 'success' : 'danger'}
                  label={
                    row.active ? t('credentials.status.active') : t('credentials.status.revoked')
                  }
                />
              </span>,
              row.active ? (
                <span
                  key={`actions-${row.id}`}
                  slot={`cell-actions-${index}`}
                  className="simsm-credentials__row-actions"
                >
                  <MudButton
                    shape="circular"
                    variant="neutral"
                    size="sm"
                    disabled={regenerate.isPending || revoke.isPending}
                    onClick={async () => {
                      const ok = await confirm({
                        title: t('mconnect_creds.regenerate.title', { clientId: row.clientId }),
                        body: t('mconnect_creds.regenerate.body'),
                        confirmLabel: t('credentials.regenerate.confirm'),
                      });
                      if (ok) regenerate.mutate(row);
                    }}
                  >
                    {t('credentials.action.regenerate')}
                  </MudButton>
                  <MudButton
                    shape="circular"
                    variant="neutral"
                    size="sm"
                    disabled={regenerate.isPending || revoke.isPending}
                    onClick={async () => {
                      const ok = await confirm({
                        title: t('confirm.revoke.title'),
                        body: t('credentials.revoke_confirm', { clientId: row.clientId }),
                        confirmLabel: t('confirm.action.revoke'),
                        destructive: true,
                      });
                      if (ok) revoke.mutate(row.clientId);
                    }}
                  >
                    {t('credentials.action.revoke')}
                  </MudButton>
                </span>
              ) : null,
            ])}
          </MudTable>
        </div>
      )}

      <MudModal
        closeLabel={t('common.close')}
        open={emitOpen}
        size="md"
        titleText={t('mconnect_creds.modal.title')}
        // AGE-BUG(mudClose-bubbles) · vendor/age-design/SOURCE_COMMIT 7322a157 — applied to
        // every modal uniformly; a no-op here until a descendant emits its own mudClose.
        onMudClose={onModalDismiss(() => setEmitOpen(false))}
      >
        <div className="simsm-credentials__modal-body">
          {/* The fields carry their own labels: a light-DOM <label> around a
              mud-text-input reaches no control, the native input lives in the
              shadow root. */}
          <MudTextInput
            size="md"
            label={t('mconnect_creds.modal.client_id_label')}
            value={clientId}
            placeholder={t('mconnect_creds.modal.client_id_placeholder')}
            onMudInput={(e: CustomEvent<{ value: string }>) => setClientId(e.detail.value)}
          />
          <MudTextarea
            label={t('credentials.modal.allowed_ips_label')}
            rows={3}
            resize="vertical"
            value={allowedIps}
            placeholder={t('credentials.modal.allowed_ips_placeholder')}
            onMudInput={(e: CustomEvent<{ value: string }>) => setAllowedIps(e.detail.value)}
          />
          <MudTextarea
            label={t('credentials.modal.allowed_cidrs_label')}
            rows={2}
            resize="vertical"
            value={allowedCidrs}
            placeholder={t('credentials.modal.allowed_cidrs_placeholder')}
            onMudInput={(e: CustomEvent<{ value: string }>) => setAllowedCidrs(e.detail.value)}
          />
          {formError ? (
            <MudInfoBox emphasis="strong" role="alert" variant="error">
              {formError}
            </MudInfoBox>
          ) : null}
        </div>
        <div slot="actions" className="simsm-credentials__modal-actions">
          <MudButton
            shape="circular"
            variant="neutral"
            size="md"
            onClick={() => setEmitOpen(false)}
          >
            {t('credentials.modal.cancel')}
          </MudButton>
          <MudButton
            shape="circular"
            variant="primary"
            size="md"
            disabled={create.isPending}
            onClick={submit}
          >
            {t('credentials.modal.generate')}
          </MudButton>
        </div>
      </MudModal>

      <MudModal
        closeLabel={t('common.close')}
        open={createdSecret !== null}
        size="md"
        titleText={
          createdSecret?.reissued
            ? t('credentials.secret.title')
            : t('credentials.modal.created.title')
        }
        // AGE-BUG(mudClose-bubbles) · vendor/age-design/SOURCE_COMMIT 7322a157 — applied to
        // every modal uniformly; a no-op here until a descendant emits its own mudClose.
        onMudClose={onModalDismiss(() => setCreatedSecret(null))}
      >
        {createdSecret ? (
          <div className="simsm-credentials__created">
            <MudInfoBox emphasis="strong" variant="warning">
              {t('credentials.modal.created.warning')}
            </MudInfoBox>
            <div className="simsm-credentials__created-row">
              <span>{t('credentials.modal.created.client_id')}</span>
              <code>{createdSecret.credential.clientId}</code>
              <MudButton
                shape="circular"
                variant="neutral"
                size="sm"
                onClick={() => copyToClipboard(createdSecret.credential.clientId)}
              >
                {t('credentials.modal.created.copy')}
              </MudButton>
            </div>
            <div className="simsm-credentials__created-row">
              <span>{t('credentials.modal.created.secret')}</span>
              <code className="simsm-credentials__secret">{createdSecret.clientSecret}</code>
              <MudButton
                shape="circular"
                variant="neutral"
                size="sm"
                onClick={() => copyToClipboard(createdSecret.clientSecret)}
              >
                {copied
                  ? t('credentials.modal.created.copied')
                  : t('credentials.modal.created.copy')}
              </MudButton>
            </div>
          </div>
        ) : null}
        <div slot="actions" className="simsm-credentials__modal-actions">
          <MudButton
            shape="circular"
            variant="primary"
            size="md"
            onClick={() => setCreatedSecret(null)}
          >
            {t('credentials.modal.created.close')}
          </MudButton>
        </div>
      </MudModal>
    </section>
  );
}
